import React, { useState, useMemo, useEffect } from "react";
import AdminPanel from "./components/AdminPanel";
import AboutPage from "./components/AboutPage";
import ValidationPage from "./components/ValidationPage";
import WalkthroughModal from "./components/WalkthroughModal";
import "./brand-tokens.css";
import "./App.css";

// ── Default lipid component library (used until /api/lipid-db fetch resolves) ─
// logP: PubChem XLogP3 / literature; HSP: group-contribution estimates (MPa½)
const DEFAULT_LIPID_DB = {
  solid_lipids: [
    { id: "glyc_caprylate", name: "Glyceryl caprylate (C8)",      logp: 3.5,  delta_d: 16.8, delta_p: 5.0, delta_h: 12.3 },
    { id: "glyc_caprate",   name: "Glyceryl caprate (C10)",        logp: 4.3,  delta_d: 16.9, delta_p: 4.5, delta_h: 11.6 },
    { id: "compritol",      name: "Glyceryl behenate (Compritol)", logp: 9.7,  delta_d: 17.0, delta_p: 2.0, delta_h: 4.0  },
    { id: "cetyl_palm",     name: "Cetyl palmitate",               logp: 11.6, delta_d: 16.5, delta_p: 1.5, delta_h: 3.5  },
    { id: "stearic_acid",   name: "Stearic acid",                  logp: 8.2,  delta_d: 17.3, delta_p: 3.0, delta_h: 5.2  },
    { id: "tripalmitin",    name: "Tripalmitin",                   logp: 10.9, delta_d: 17.2, delta_p: 2.0, delta_h: 3.8  },
  ],
  liquid_lipids: [
    { id: "soy_lecithin", name: "Soy lecithin",                          logp: 4.5,  delta_d: 16.5, delta_p: 6.5, delta_h: 10.0 },
    { id: "oleic_acid",   name: "Oleic acid",                            logp: 7.6,  delta_d: 16.0, delta_p: 2.5, delta_h: 4.5  },
    { id: "cc_trig",      name: "Caprylic/capric triglyceride (C8/C10)", logp: 7.8,  delta_d: 16.8, delta_p: 1.2, delta_h: 3.0  },
    { id: "squalene",     name: "Squalene",                              logp: 10.6, delta_d: 16.0, delta_p: 0.3, delta_h: 1.5  },
  ],
  surfactants: [
    { id: "ps80",            name: "Polysorbate 80",   logp: 2.45, delta_d: 16.5, delta_p: 5.0, delta_h: 11.0 },
    { id: "peg100_stearate", name: "PEG-100 stearate", logp: 2.1,  delta_d: 17.0, delta_p: 3.0, delta_h: 9.5  },
    { id: "polox188",        name: "Poloxamer 188",    logp: 1.5,  delta_d: 17.2, delta_p: 8.0, delta_h: 10.5 },
  ],
};
const CUSTOM_ENTRY = (id) => ({ id, name: "Custom", logp: null, delta_d: null, delta_p: null, delta_h: null, custom: true });

function App() {
  // ── Page routing ─────────────────────────────────────────────────────────
  const [page, setPage] = useState("tool");

  // ── Admin panel ───────────────────────────────────────────────────────────
  const [showAdmin, setShowAdmin]         = useState(false);
  const [adminKey, setAdminKey]           = useState("");
  const [showKeyModal, setShowKeyModal]   = useState(false);
  const [adminKeyInput, setAdminKeyInput] = useState("");

  const openAdmin = () => {
    if (adminKey) { setShowAdmin(true); return; }
    setShowKeyModal(true);
  };
  const submitKey = () => {
    if (!adminKeyInput.trim()) return;
    setAdminKey(adminKeyInput.trim());
    setShowKeyModal(false);
    setShowAdmin(true);
  };

  // ── Walkthrough ───────────────────────────────────────────────────────────
  const [showWalkthrough, setShowWalkthrough] = useState(true);

  // ── Reset ─────────────────────────────────────────────────────────────────
  const handleReset = () => { setResults(null); setError(null); };

  // ── Drug name / SMILES → Log P lookup ────────────────────────────────────
  const [drugNameInput, setDrugNameInput] = useState("");
  const [smilesInput,   setSmilesInput]   = useState("");
  const [logpCalcState, setLogpCalcState] = useState("idle");
  const [logpCalcMsg,   setLogpCalcMsg]   = useState("");

  const applyLogP = (logp, source) => {
    setDrugProps({ logp, delta_d: "", delta_p: "", delta_h: "" });
    setSelectedDrug(""); setResults(null); setError(null);
    setLogpCalcState("success");
    setLogpCalcMsg(`Log P = ${logp} — ${source}`);
    setTimeout(() => setLogpCalcState("idle"), 7000);
  };

  const handleNameLookup = async () => {
    const name = drugNameInput.trim();
    if (!name) return;
    setLogpCalcState("loading"); setLogpCalcMsg("");
    try {
      const enc = encodeURIComponent(name);
      const url = `https://pubchem.ncbi.nlm.nih.gov/rest/pug/compound/name/${enc}/property/XLogP,IsomericSMILES,IUPACName/JSON`;
      const res = await fetch(url);
      if (res.status === 404) throw new Error(`"${name}" not found in PubChem.`);
      if (!res.ok) throw new Error("PubChem returned an error — try again.");
      const data = await res.json();
      const props = data?.PropertyTable?.Properties?.[0];
      if (!props) throw new Error("No data returned.");
      if (props.XLogP == null) throw new Error("No XLogP value — try entering Log P manually.");
      if (props.IsomericSMILES) setSmilesInput(props.IsomericSMILES);
      applyLogP(parseFloat(props.XLogP.toFixed(2)), `PubChem XLogP3 · ${props.IUPACName || name}`);
    } catch (err) { setLogpCalcState("error"); setLogpCalcMsg(err.message); }
  };

  const handleCalcLogP = async () => {
    const smiles = smilesInput.trim();
    if (!smiles) return;
    setLogpCalcState("loading"); setLogpCalcMsg("");
    try {
      const enc = encodeURIComponent(smiles);
      const url = `https://pubchem.ncbi.nlm.nih.gov/rest/pug/compound/smiles/${enc}/property/XLogP,IUPACName/JSON`;
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        const props = data?.PropertyTable?.Properties?.[0];
        if (props?.XLogP != null) {
          applyLogP(parseFloat(props.XLogP.toFixed(2)), `PubChem XLogP3${props.IUPACName ? ` · ${props.IUPACName}` : ""}`);
          return;
        }
      }
      const API_URL = process.env.REACT_APP_API_URL || "";
      const apiRes  = await fetch(`${API_URL}/api/logp`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ smiles }),
      });
      const apiData = await apiRes.json();
      if (!apiRes.ok) throw new Error(apiData.error || "Calculation failed");
      applyLogP(apiData.logp, apiData.source);
    } catch (err) {
      setLogpCalcState("error");
      setLogpCalcMsg(err.message.includes("fetch") ? "Could not reach lookup service." : err.message);
    }
  };

  // ── Drug props state ──────────────────────────────────────────────────────
  const [selectedDrug, setSelectedDrug] = useState("");
  const [drugProps, setDrugProps]       = useState({ logp: "", delta_d: "", delta_p: "", delta_h: "" });
  const [results, setResults]           = useState(null);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState(null);

  const validationDrugs = {
    pyrene:   { name: "Pyrene",   logp: 5.19, hsp: { delta_d: 20.4, delta_p: 5.0, delta_h: 3.5 } },
    nile_red: { name: "Nile Red", logp: 4.0,  hsp: { delta_d: 19.8, delta_p: 6.5, delta_h: 5.2 } },
  };

  const handleDrugSelect = (key) => {
    setSelectedDrug(key); setResults(null); setError(null);
    const drug = validationDrugs[key];
    if (!drug) { setDrugProps({ logp: "", delta_d: "", delta_p: "", delta_h: "" }); return; }
    setDrugProps({ logp: drug.logp, delta_d: drug.hsp.delta_d, delta_p: drug.hsp.delta_p, delta_h: drug.hsp.delta_h });
  };

  const handlePropChange = (field, value) => {
    setSelectedDrug("");
    setDrugProps(prev => ({ ...prev, [field]: value }));
  };

  // ── Lipid database (fetched from backend; falls back to built-in defaults) ─
  const [lipidDb, setLipidDb] = useState(DEFAULT_LIPID_DB);
  useEffect(() => {
    const API_URL = process.env.REACT_APP_API_URL || "";
    fetch(`${API_URL}/api/lipid-db`)
      .then(r => r.ok ? r.json() : null)
      .then(db => { if (db && db.solid_lipids) setLipidDb(db); })
      .catch(() => {}); // silently keep defaults on network error
  }, []);

  // Derived component lists (from fetched DB + Custom entry appended)
  const SOLID_LIPIDS  = useMemo(() => [...(lipidDb.solid_lipids  || []), CUSTOM_ENTRY("custom_solid")], [lipidDb]);
  const LIQUID_LIPIDS = useMemo(() => [...(lipidDb.liquid_lipids || []), CUSTOM_ENTRY("custom_liq")],   [lipidDb]);
  const SURFACTANTS   = useMemo(() => [...(lipidDb.surfactants   || []), CUSTOM_ENTRY("custom_surf")],  [lipidDb]);

  // ── Formulation builder state ─────────────────────────────────────────────
  const [solidLipidId,  setSolidLipidId]  = useState("");
  const [liquidLipidId, setLiquidLipidId] = useState("");
  const [solidRatio,    setSolidRatio]    = useState(70);
  const [surfactantId,  setSurfactantId]  = useState("");

  const [customSolid,  setCustomSolid]  = useState({ logp: "", delta_d: "", delta_p: "", delta_h: "" });
  const [customLiquid, setCustomLiquid] = useState({ logp: "", delta_d: "", delta_p: "", delta_h: "" });
  const [customSurf,   setCustomSurf]   = useState({ logp: "", delta_d: "", delta_p: "", delta_h: "" });

  const selectedSolid  = SOLID_LIPIDS.find(l => l.id === solidLipidId)  || null;
  const selectedLiquid = LIQUID_LIPIDS.find(l => l.id === liquidLipidId) || null;
  const selectedSurf   = SURFACTANTS.find(s  => s.id === surfactantId)   || null;

  const resolveProps = (sel, custom) => sel?.custom
    ? { ...sel, logp: parseFloat(custom.logp)||null, delta_d: parseFloat(custom.delta_d)||null, delta_p: parseFloat(custom.delta_p)||null, delta_h: parseFloat(custom.delta_h)||null }
    : sel;

  const solidProps  = resolveProps(selectedSolid,  customSolid);
  const liquidProps = resolveProps(selectedLiquid, customLiquid);
  const surfProps   = resolveProps(selectedSurf,   customSurf);

  const nlcProps = useMemo(() => {
    if (!solidProps || !liquidProps || !surfProps) return null;
    if (solidProps.logp == null || liquidProps.logp == null || surfProps.logp == null) return null;
    const sr = solidRatio / 100, lr = 1 - sr;
    const coreLogP = +(sr * solidProps.logp + lr * liquidProps.logp).toFixed(2);
    return {
      core_logp: coreLogP,
      surf_logp: surfProps.logp,
      core_hsp: {
        delta_d: +(sr * solidProps.delta_d + lr * liquidProps.delta_d).toFixed(2),
        delta_p: +(sr * solidProps.delta_p + lr * liquidProps.delta_p).toFixed(2),
        delta_h: +(sr * solidProps.delta_h + lr * liquidProps.delta_h).toFixed(2),
      },
      surf_hsp: { delta_d: surfProps.delta_d, delta_p: surfProps.delta_p, delta_h: surfProps.delta_h },
      grad: +(coreLogP - surfProps.logp).toFixed(2),
      name: [
        selectedSolid?.custom  ? "Custom solid"  : selectedSolid?.name?.split("(")[0].trim(),
        selectedLiquid?.custom ? "Custom liquid" : selectedLiquid?.name?.split("(")[0].trim(),
        selectedSurf?.custom   ? "Custom surf"   : selectedSurf?.name,
      ].filter(Boolean).join(" / "),
    };
  }, [solidProps, liquidProps, surfProps, solidRatio, selectedSolid, selectedLiquid, selectedSurf]);

  // ── Prediction ────────────────────────────────────────────────────────────
  const handlePredict = async () => {
    if (!drugProps.logp) { setError("Please enter a Log P value."); return; }
    if (!nlcProps) { setError("Please select all three lipid components."); return; }
    setLoading(true); setError(null);

    const hspProvided = drugProps.delta_d || drugProps.delta_p || drugProps.delta_h;
    const drug_hsp = hspProvided
      ? { delta_d: parseFloat(drugProps.delta_d), delta_p: parseFloat(drugProps.delta_p), delta_h: parseFloat(drugProps.delta_h) }
      : null;

    const API_URL = process.env.REACT_APP_API_URL || "";
    try {
      const res = await fetch(`${API_URL}/api/predict`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          drug_logp: parseFloat(drugProps.logp),
          drug_hsp,
          custom_formulation: {
            name:      nlcProps.name,
            core_logp: nlcProps.core_logp,
            surf_logp: nlcProps.surf_logp,
            core_hsp:  nlcProps.core_hsp,
            surf_hsp:  nlcProps.surf_hsp,
          },
        }),
      });
      if (!res.ok) throw new Error(`Server error ${res.status}`);
      setResults(await res.json());
    } catch (err) {
      setError(`Could not reach the backend: ${err.message}`);
    }
    setLoading(false);
  };

  // ── LogP gauge helpers ────────────────────────────────────────────────────
  const logpValue    = parseFloat(drugProps.logp);
  const gaugePercent = !isNaN(logpValue)
    ? Math.max(0, Math.min(100, ((logpValue + 2) / 10) * 100))
    : null;
  const getLogPLabel  = v => isNaN(v) ? "" : v < 0 ? "Very Hydrophilic" : v < 2 ? "Hydrophilic" : v < 3 ? "Low Lipophilicity" : v < 5 ? "Moderate Lipophilicity" : "Highly Lipophilic";
  const getLogPColour = v => isNaN(v) ? "var(--ink-soft)" : v < 1 ? "var(--blue-600)" : v < 3 ? "var(--green-500)" : v < 5 ? "var(--gold-600)" : "var(--red-600)";

  // ── Small component helpers ───────────────────────────────────────────────
  const PropBadge = ({ comp }) => comp?.logp != null ? (
    <div className="comp-prop-badge">
      <span className="cpb-item cpb-logp">Log P {comp.logp}</span>
      {comp.delta_d != null && <span className="cpb-item cpb-hsp">δd {comp.delta_d} · δp {comp.delta_p} · δh {comp.delta_h} MPa½</span>}
    </div>
  ) : null;

  const CustomInputs = ({ state, setState, label }) => (
    <div className="custom-inputs">
      <p className="custom-inputs-label">Enter {label} physicochemical properties:</p>
      <div className="custom-inputs-grid">
        {[["logp","Log P","e.g. 8.5","required"],["delta_d","δd (MPa½)","e.g. 17.0",""],["delta_p","δp (MPa½)","e.g. 3.0",""],["delta_h","δh (MPa½)","e.g. 3.5",""]].map(([field, lbl, ph, req]) => (
          <div className="input-group" key={field}>
            <label>{lbl}{req && <span className="required-label"> {req}</span>}</label>
            <input type="number" step="0.1" placeholder={ph}
              value={state[field]}
              onChange={e => setState(s => ({ ...s, [field]: e.target.value }))} />
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <>
      {showWalkthrough && <WalkthroughModal onClose={() => setShowWalkthrough(false)} />}

      {/* ── TOOLBAR (slim, sticky, light — app-tool archetype) ──────────── */}
      <div className="site-toolbar">
        <div className="toolbar-inner">
          <div className="toolbar-brand">
            <img className="brand-icon-img" src="/logo.svg" alt="Lipid Logic logo" />
            <span className="brand-name">
              <span className="brand-name-plain">Lipid</span><span className="brand-name-accent">Logic</span>
            </span>
          </div>
          <nav className="main-nav">
            <button className={`nav-link ${page === "tool" ? "nav-link--active" : ""}`} onClick={() => setPage("tool")}>Tool</button>
            <button className={`nav-link ${page === "about" ? "nav-link--active" : ""}`} onClick={() => setPage("about")}>About</button>
            <button className={`nav-link ${page === "validation" ? "nav-link--active" : ""}`} onClick={() => setPage("validation")}>Validation</button>
            <button className="nav-guide-btn" onClick={() => setShowWalkthrough(true)} title="Re-open guide">? Guide</button>
            <button className="nav-admin-btn" onClick={openAdmin} title="Admin panel">⚙</button>
          </nav>
          <a className="toolbar-lab-link" href="https://carmali-lab.com/" target="_blank" rel="noopener noreferrer">
            <img className="toolbar-lab-icon" src="https://carmali-lab.com/images/logo.png" alt="Carmali Lab logo" />
            <span className="label">Carmali Lab</span>
          </a>
        </div>
      </div>

      {/* ── HERO (not sticky, sits below the toolbar) ───────────────────── */}
      <header className="hero">
        <div className="hero-content">
          <div className="hero-badge">Computational Tool for Rational NLC Design</div>
          <h1 className="hero-title">NLC Formulation Predictor</h1>
          <p className="hero-subtitle">
            Predict drug partitioning within nanostructured lipid carriers using lipophilicity and Hansen solubility parameters.
          </p>
          <p className="hero-model-line">
            <span className="hero-model-label">Model inputs:</span> log P &bull; Hansen dispersion (&delta;D) &bull; polar (&delta;P) &bull; hydrogen bonding (&delta;H)
            &nbsp;&nbsp;|&nbsp;&nbsp;
            <span className="hero-model-label">Output:</span> Predicted drug distribution between lipid phases.
          </p>
        </div>
      </header>

      {/* ── WORKFLOW STRIP (hidden on about/validation pages) ────────────── */}
      <div className={`workflow-strip${page !== "tool" ? " workflow-strip--hidden" : ""}`}>
        <div className="workflow-inner">
          <div className="workflow-step">
            <div className="workflow-num">1</div>
            <div className="workflow-text">
              <span className="workflow-title">Enter drug physicochemical properties</span>
              <span className="workflow-desc">Log P and Hansen solubility parameters</span>
            </div>
          </div>
          <div className="workflow-arrow">→</div>
          <div className="workflow-step">
            <div className="workflow-num">2</div>
            <div className="workflow-text">
              <span className="workflow-title">Define lipid carrier composition</span>
              <span className="workflow-desc">Solid lipid, liquid lipid, surfactant</span>
            </div>
          </div>
          <div className="workflow-arrow">→</div>
          <div className="workflow-step">
            <div className="workflow-num">3</div>
            <div className="workflow-text">
              <span className="workflow-title">Predict drug localisation</span>
              <span className="workflow-desc">Within the NLC structure</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── ADMIN KEY MODAL ─────────────────────────────────────────────── */}
      {showKeyModal && (
        <div className="modal-overlay" onClick={() => setShowKeyModal(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <h3 className="modal-title">Admin Access</h3>
            <p className="modal-desc">Enter the admin key to manage the formulation database.</p>
            <input className="modal-input" type="password" placeholder="Admin key"
              value={adminKeyInput} onChange={e => setAdminKeyInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && submitKey()} autoFocus />
            <div className="modal-actions">
              <button className="modal-cancel" onClick={() => setShowKeyModal(false)}>Cancel</button>
              <button className="modal-confirm" onClick={submitKey}>Open Admin Panel</button>
            </div>
          </div>
        </div>
      )}

      {showAdmin && <AdminPanel adminKey={adminKey} onClose={() => setShowAdmin(false)} />}

      {/* ── ABOUT / VALIDATION ──────────────────────────────────────────── */}
      {page === "about"      && <AboutPage />}
      {page === "validation" && <ValidationPage />}

      {/* ── TOOL PAGE ───────────────────────────────────────────────────── */}
      {page === "tool" && (
        <div className="app-container">
          <div className="main-grid">

            {/* ═══════ LEFT PANEL — Drug Properties ═══════ */}
            <div className="left-panel">
              <div className="card">
                <h3 className="card-title">Drug Properties</h3>

                {/* Validation drug shortcut */}
                <div className="input-group">
                  <label>
                    Load a validation drug
                    <span className="tooltip-icon" data-tooltip="Auto-fills known properties for pyrene or Nile Red — use to test the model against validated experimental data.">?</span>
                    <span className="optional-label">optional</span>
                  </label>
                  <select value={selectedDrug} onChange={e => handleDrugSelect(e.target.value)}>
                    <option value="">— Select —</option>
                    {Object.keys(validationDrugs).map(k => (
                      <option key={k} value={k}>{validationDrugs[k].name}</option>
                    ))}
                  </select>
                  {selectedDrug && (
                    <p className="input-hint">✓ Properties auto-filled.{" "}
                      <button className="inline-link" onClick={() => setPage("validation")}>See validation data →</button>
                    </p>
                  )}
                </div>

                <div className="or-divider"><span>or search by name / SMILES</span></div>

                {/* Name / SMILES lookup */}
                <div className="smiles-section">
                  <label className="lookup-field-label">By drug name</label>
                  <div className="smiles-input-row">
                    <input className="smiles-input" type="text" placeholder="e.g. ibuprofen, cyclosporine A"
                      value={drugNameInput}
                      onChange={e => { setDrugNameInput(e.target.value); setLogpCalcState("idle"); }}
                      onKeyDown={e => e.key === "Enter" && handleNameLookup()} />
                    <button className={`smiles-calc-btn ${logpCalcState === "loading" ? "smiles-calc-btn--loading" : ""}`}
                      onClick={handleNameLookup} disabled={!drugNameInput.trim() || logpCalcState === "loading"}>
                      {logpCalcState === "loading" ? <><span className="spinner" /> Looking up…</> : "Look Up"}
                    </button>
                  </div>
                  <label className="lookup-field-label lookup-field-label--mt">By SMILES string</label>
                  <div className="smiles-input-row">
                    <input className="smiles-input" type="text" placeholder="e.g. c1ccccc1"
                      value={smilesInput}
                      onChange={e => { setSmilesInput(e.target.value); setLogpCalcState("idle"); }}
                      onKeyDown={e => e.key === "Enter" && handleCalcLogP()}
                      spellCheck={false} autoCorrect="off" autoCapitalize="none" />
                    <button className={`smiles-calc-btn ${logpCalcState === "loading" ? "smiles-calc-btn--loading" : ""}`}
                      onClick={handleCalcLogP} disabled={!smilesInput.trim() || logpCalcState === "loading"}>
                      {logpCalcState === "loading" ? <><span className="spinner" /> Calculating…</> : "Calculate"}
                    </button>
                  </div>
                  {logpCalcState === "success" && <p className="smiles-feedback smiles-feedback--success">✓ {logpCalcMsg}</p>}
                  {logpCalcState === "error"   && <p className="smiles-feedback smiles-feedback--error">⚠ {logpCalcMsg}</p>}
                </div>

                {/* Log P */}
                <div className="input-group">
                  <label>
                    Log P
                    <span className="tooltip-icon" data-tooltip="Octanol–water partition coefficient. Higher = more lipophilic. The only required input.">?</span>
                    <span className="required-label">required</span>
                  </label>
                  <input type="number" step="0.01" placeholder="e.g. 3.97"
                    value={drugProps.logp} onChange={e => handlePropChange("logp", e.target.value)} />
                  <p className="input-hint">Find in ChemDraw, DrugBank, or PubChem</p>
                </div>

                {gaugePercent !== null && (
                  <div className="logp-gauge-container">
                    <div className="gauge">
                      <div className="gauge-dot" style={{ left: `calc(${gaugePercent}% - 10px)` }} />
                    </div>
                    <div className="gauge-labels">
                      <span>Hydrophilic</span>
                      <span className="gauge-value-label" style={{ color: getLogPColour(logpValue) }}>{getLogPLabel(logpValue)}</span>
                      <span>Lipophilic</span>
                    </div>
                  </div>
                )}

                {/* HSP */}
                <div className="hsp-section">
                  <div className="hsp-header">
                    <h4>
                      Hansen Solubility Parameters
                      <span className="tooltip-icon" data-tooltip="Decompose solubility into dispersive (δd), polar (δp), and H-bonding (δh). Units: MPa½. Enable precise three-hypothesis prediction.">?</span>
                      <span className="optional-label">optional</span>
                    </h4>
                    {(drugProps.delta_d || drugProps.delta_p || drugProps.delta_h) ? (
                      <p className="hsp-hint hsp-hint--active">✓ Full prediction mode — lipophilicity + Hansen compatibility</p>
                    ) : (
                      <p className="hsp-hint">
                        <strong>Log P only prediction</strong><br/>
                        Add Hansen parameters (δD, δP, δH) to enable full compatibility modelling.
                      </p>
                    )}
                  </div>
                  <div className="hsp-grid">
                    {[["delta_d","δd (MPa½)","Dispersion","e.g. 18.0"],["delta_p","δp (MPa½)","Polar","e.g. 5.5"],["delta_h","δh (MPa½)","H-bonding","e.g. 8.5"]].map(([field, lbl, tip, ph]) => (
                      <div className="input-group" key={field}>
                        <label>{lbl}<span className="tooltip-icon" data-tooltip={tip}>?</span></label>
                        <input type="number" step="0.1" placeholder={ph}
                          value={drugProps[field]} onChange={e => handlePropChange(field, e.target.value)} />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* ═══════ MIDDLE PANEL — Formulation Builder ═══════ */}
            <div className="middle-panel">
              <div className="card">
                <h3 className="card-title">Lipid Composition</h3>

                {/* Solid lipid */}
                <div className="input-group">
                  <label>
                    Solid Lipid
                    <span className="tooltip-icon" data-tooltip="Forms the crystalline matrix of the NLC core. Sets the core lipophilicity and particle rigidity.">?</span>
                  </label>
                  <select value={solidLipidId} onChange={e => { setSolidLipidId(e.target.value); setResults(null); }}>
                    <option value="">— Select solid lipid —</option>
                    {SOLID_LIPIDS.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                  {selectedSolid?.custom
                    ? <CustomInputs state={customSolid} setState={setCustomSolid} label="solid lipid" />
                    : <PropBadge comp={solidProps} />}
                </div>

                {/* Liquid lipid */}
                <div className="input-group">
                  <label>
                    Liquid Lipid
                    <span className="tooltip-icon" data-tooltip="Dispersed liquid oil disrupts crystallinity to create nanoscale drug-trapping pockets.">?</span>
                  </label>
                  <select value={liquidLipidId} onChange={e => { setLiquidLipidId(e.target.value); setResults(null); }}>
                    <option value="">— Select liquid lipid —</option>
                    {LIQUID_LIPIDS.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                  {selectedLiquid?.custom
                    ? <CustomInputs state={customLiquid} setState={setCustomLiquid} label="liquid lipid" />
                    : <PropBadge comp={liquidProps} />}
                </div>

                {/* Ratio slider — only when both lipids are valid */}
                {solidProps?.logp != null && liquidProps?.logp != null && (
                  <div className="ratio-section">
                    <label className="ratio-label">
                      Solid : Liquid ratio
                      <span className="tooltip-icon" data-tooltip="Typical NLC formulations use 70–90% solid lipid. Higher solid content increases crystallinity and matrix stability.">?</span>
                    </label>
                    <input className="ratio-slider" type="range" min="50" max="90" step="5"
                      value={solidRatio}
                      onChange={e => { setSolidRatio(parseInt(e.target.value)); setResults(null); }} />
                    <div className="ratio-bar-row">
                      <div className="ratio-solid-bar" style={{ width: `${solidRatio}%` }} />
                      <div className="ratio-liquid-bar" style={{ width: `${100 - solidRatio}%` }} />
                    </div>
                    <div className="ratio-text-row">
                      <span className="ratio-solid-label">Solid {solidRatio}%</span>
                      <span className="ratio-liquid-label">Liquid {100 - solidRatio}%</span>
                    </div>
                  </div>
                )}

                {/* Surfactant */}
                <div className="input-group">
                  <label>
                    Surfactant
                    <span className="tooltip-icon" data-tooltip="Coats the NLC surface forming an amphiphilic corona. Its log P and HSP define the interface affinity.">?</span>
                  </label>
                  <select value={surfactantId} onChange={e => { setSurfactantId(e.target.value); setResults(null); }}>
                    <option value="">— Select surfactant —</option>
                    {SURFACTANTS.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                  {selectedSurf?.custom
                    ? <CustomInputs state={customSurf} setState={setCustomSurf} label="surfactant" />
                    : <PropBadge comp={surfProps} />}
                </div>

              </div>
            </div>

            {/* ═══════ RIGHT PANEL — Results ═══════ */}
            <div className="right-panel">
              {/* Step 3 — Predict */}
              {!results && (
                <div className="predict-step-box">
                  {error && <div className="error-message">{error}</div>}
                  <button className="predict-button"
                    onClick={handlePredict}
                    disabled={loading || !drugProps.logp || !nlcProps}>
                    {loading ? <><span className="spinner" /> Analysing…</> : "▶ Predict Localisation"}
                  </button>
                  <p className="predict-hint">
                    {!drugProps.logp ? "↑ Enter drug Log P first" : !nlcProps ? "↑ Select all three components" : "✓ Ready — click Predict Localisation"}
                  </p>
                </div>
              )}

              {results && results.metadata.too_hydrophilic ? (
                <div className="hydrophilic-panel">
                  <div className="hydrophilic-icon">💧</div>
                  <h3>NLC Not Recommended</h3>
                  <p className="hydrophilic-intro">
                    With a Log P of <strong>{parseFloat(drugProps.logp).toFixed(2)}</strong>, this drug is
                    too hydrophilic for reliable NLC encapsulation. The drug will predominantly remain
                    in the aqueous phase rather than partitioning into the lipid matrix.
                  </p>
                  <div className="hydrophilic-alternatives">
                    <h4>Recommended alternatives</h4>
                    {[
                      { icon:"🫧", title:"Liposomes", desc:"Phospholipid bilayer vesicles with an aqueous core — well-suited for hydrophilic drugs." },
                      { icon:"🔵", title:"Polymeric Nanoparticles", desc:"PLGA or chitosan-based systems can entrap hydrophilic drugs via physical or ionic interactions." },
                      { icon:"⭕", title:"Cyclodextrin Complexation", desc:"Inclusion complexes that improve solubility and stability without a lipid matrix." },
                    ].map(a => (
                      <div className="alt-card" key={a.title}>
                        <span className="alt-icon">{a.icon}</span>
                        <div><strong>{a.title}</strong><p>{a.desc}</p></div>
                      </div>
                    ))}
                  </div>
                </div>

              ) : results ? (() => {
                const result  = results.results[0];
                const isCore  = result.location === "Core";

                // Hansen compatibility rating helper
                const hansCompat = (ra) => {
                  if (ra == null) return null;
                  if (ra < 5.0)  return { label: "Excellent compatibility with core lipid", cls: "hc--excellent" };
                  if (ra < 8.0)  return { label: "Good compatibility with core lipid",      cls: "hc--good"      };
                  if (ra < 10.0) return { label: "Moderate compatibility with core lipid",  cls: "hc--moderate"  };
                  return              { label: "Poor compatibility with core lipid",         cls: "hc--poor"      };
                };
                const coreCompat = hansCompat(result.d_core);

                // ΔlogP gradient (core vs interface, same as the old preview)
                const gradAbs  = nlcProps ? Math.abs(nlcProps.grad) : null;
                const gradDir  = nlcProps ? (nlcProps.grad > 0 ? "Core-favoured" : nlcProps.grad < 0 ? "Interface-favoured" : "No gradient") : null;

                return (
                  <div className="single-result">

                    {/* 1 — Predicted Localisation */}
                    <div className={`location-hero ${isCore ? "lh--core" : "lh--interface"}`}>
                      <div className="lh-icon">{isCore ? "🔵" : "🟢"}</div>
                      <div className="lh-body">
                        <p className="lh-label">Predicted Localisation</p>
                        <h2 className="lh-site">{isCore ? "Lipid Core" : "Surfactant Interface"}</h2>
                        <p className="lh-implication">
                          {isCore
                            ? "Slower release · Protected from environment"
                            : "Faster release · Surface-driven distribution"}
                        </p>
                      </div>
                    </div>

                    {/* 2 — NLC Cross-Section */}
                    <div className="spatial-viz">
                      <p className="spatial-title">NLC Cross-Section</p>
                      <svg className="nlc-svg" viewBox="0 0 200 210" xmlns="http://www.w3.org/2000/svg" aria-label="NLC cross-section diagram">
                        <defs>
                          <radialGradient id="nlcShellGrad" cx="35%" cy="35%">
                            <stop offset="0%" stopColor="#e8f0fb"/>
                            <stop offset="100%" stopColor="#4a90b8"/>
                          </radialGradient>
                          <radialGradient id="nlcCoreGrad" cx="35%" cy="35%">
                            <stop offset="0%" stopColor="#4a90b8"/>
                            <stop offset="100%" stopColor="#1e5faa"/>
                          </radialGradient>
                        </defs>
                        <text x="100" y="10" textAnchor="middle" fill="#8593a3" fontSize="7.5" fontStyle="italic">Aqueous phase</text>
                        <circle cx="100" cy="110" r="85" fill="url(#nlcShellGrad)" stroke="#1e5faa" strokeWidth="1.5"/>
                        <circle cx="100" cy="110" r="50" fill="url(#nlcCoreGrad)"/>
                        <text x="100" y="124" textAnchor="middle" fill="white" fontSize="9.5" fontWeight="700" letterSpacing="0.5">LIPID CORE</text>
                        <text x="157" y="140" textAnchor="middle" fill="white" fontSize="7.5" fontWeight="600" opacity="0.95">Surfactant</text>
                        <text x="157" y="151" textAnchor="middle" fill="white" fontSize="7.5" fontWeight="600" opacity="0.95">Shell</text>
                        {isCore ? (
                          <>
                            <circle cx="100" cy="91" r="10" fill="#c4392b" stroke="white" strokeWidth="2.5"/>
                            <text x="100" y="78" textAnchor="middle" fill="#c4392b" fontSize="8.5" fontWeight="700">DRUG</text>
                          </>
                        ) : (
                          <>
                            <circle cx="100" cy="30" r="10" fill="#c4392b" stroke="white" strokeWidth="2.5"/>
                            <text x="100" y="47" textAnchor="middle" fill="#c4392b" fontSize="8.5" fontWeight="700">DRUG</text>
                          </>
                        )}
                      </svg>
                      <div className="nlc-legend">
                        <span className="nlc-legend-item"><span className="nlc-swatch nlc-swatch--core"/>Lipid Core</span>
                        <span className="nlc-legend-item"><span className="nlc-swatch nlc-swatch--shell"/>Surfactant Shell</span>
                        <span className="nlc-legend-item"><span className="nlc-swatch nlc-swatch--drug"/>Drug</span>
                      </div>
                    </div>

                    {/* 3 — Partitioning Drivers */}
                    {(gradAbs !== null || coreCompat) && (
                      <div className="partitioning-drivers">
                        <h4 className="pd-title">Partitioning Drivers</h4>
                        {gradAbs !== null && (
                          <div className="pd-row">
                            <div className="pd-icon">📊</div>
                            <div className="pd-body">
                              <span className="pd-label">Lipophilicity difference</span>
                              <span className="pd-value">ΔlogP = <strong>{gradAbs}</strong></span>
                              <span className={`pd-tag ${nlcProps.grad > 0 ? "pd-tag--core" : "pd-tag--surf"}`}>{gradDir}</span>
                            </div>
                          </div>
                        )}
                        {coreCompat && (
                          <div className="pd-row">
                            <div className="pd-icon">🧪</div>
                            <div className="pd-body">
                              <span className="pd-label">Hansen compatibility</span>
                              <span className="pd-value">Δδ = <strong>{result.d_core} MPa½</strong></span>
                              <span className={`pd-tag ${coreCompat.cls}`}>{coreCompat.label.replace(" with core lipid", "")}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* 4 — Model Confidence */}
                    <div className="card confidence-card confidence-card--inline confidence-card--compact">
                      <div className="confidence-row">
                        <span className="confidence-label">Model Confidence</span>
                        <div className="stars">
                          {Array.from({ length: 5 }, (_, i) => (
                            <span key={i} className={i < results.metadata.stars ? "star filled" : "star empty"}>★</span>
                          ))}
                        </div>
                        <p className="strategy-tag">{results.metadata.strategy}</p>
                      </div>
                    </div>

                    {/* 5 — Log P only banner (conditional) */}
                    {results.metadata.logp_only && (
                      <div className="logp-only-banner">
                        <span className="logp-only-icon">⚠️</span>
                        <div><strong>Log P only</strong> — add Hansen Solubility Parameters (δd, δp, δh) for a full three-hypothesis prediction and more precise affinity scores.</div>
                      </div>
                    )}

                    {/* 6 — New Analysis (centred, separated) */}
                    <div className="new-analysis-card">
                      <button className="new-analysis-btn" onClick={handleReset}>
                        ↺ New Analysis
                      </button>
                    </div>

                  </div>
                );
              })() : (
                /* Empty state */
                <div className="empty-state">
                  <div className="empty-icon">⚗️</div>
                  <h3>Ready to Predict</h3>
                  <p>Enter your drug properties, choose your lipid components, then click <strong>Predict Localisation</strong>.</p>
                  <div className="empty-steps">
                    {[
                      ["1","Enter Log P","Required — use name lookup or enter manually"],
                      ["2","Add HSP values","Optional — improves affinity precision"],
                      ["3","Select components","Solid lipid, liquid lipid, surfactant"],
                      ["4","Predict","Get localisation result with affinity percentages"],
                    ].map(([n, t, d]) => (
                      <div className="step" key={n}>
                        <span className="step-num">{n}</span>
                        <div><strong>{t}</strong><span>{d}</span></div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

          </div>
        </div>
      )}

      {/* ── FOOTER ────────────────────────────────────────────────────────── */}
      <footer className="site-footer">
        <div className="site-footer__inner">
          <div className="footer-cards">
            <div className="footer-bug-card">
              <div className="footer-bug-icon">🐛</div>
              <div className="footer-bug-body">
                <p className="footer-bug-title">Found a bug?</p>
                <p className="footer-bug-desc">Report issues or contribute on the project repository.</p>
                <a
                  className="footer-gh-link"
                  href="https://github.com/scarmali/lipid-logic"
                  target="_blank"
                  rel="noopener noreferrer">
                  <svg className="footer-gh-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61-.546-1.385-1.335-1.755-1.335-1.755-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12"/>
                  </svg>
                  View on GitHub
                </a>
              </div>
            </div>

            <div className="footer-bug-card footer-research-card">
              <div className="footer-bug-icon">🔬</div>
              <div className="footer-bug-body">
                <p className="footer-bug-title">Curious about our research?</p>
                <p className="footer-bug-desc">Explore the science and projects behind this tool.</p>
                <a
                  className="footer-gh-link"
                  href="https://carmali-lab.com/"
                  target="_blank"
                  rel="noopener noreferrer">
                  <svg className="footer-gh-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style={{strokeWidth:"2",stroke:"currentColor"}}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"/>
                  </svg>
                  Visit our Group Webpage
                </a>
              </div>
            </div>
          </div>

          <div className="footer-copy">
            © {new Date().getFullYear()} Lipid Logic Explorer — Computational Tool for Rational NLC Design
          </div>
        </div>
      </footer>
    </>
  );
}

export default App;
